import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useActionData, useSearchParams } from 'react-router-dom'
import Smartlook from 'smartlook-client'
import styled from 'styled-components'

import { StyledActionButton } from '@/components/Buttons.tsx'
import { Datepicker } from '@/components/Datepicker.tsx'
import { Input } from '@/components/Input.tsx'
import { InputCaption } from '@/components/Label.tsx'
import { NotificationError } from '@/components/Notification.tsx'
import {
  FormElement,
  FormStyled,
  FormSubmit,
} from '@/components/templates/Form.tsx'
import { ScreenWrapper } from '@/components/templates/ScreenWrapper.tsx'
import { Header } from '@/components/Text.tsx'
import { useTrackFormProgress } from '@/hooks/useTrackFormProgress.ts'
import { useAppointmentStore } from '@/store.ts'
import { XpertFormStep } from '@/utils/XpertFormSteps.ts'

import { ActionData } from './actions.ts'

const projectId = import.meta.env.XPERT_SMARTLOOK_KEY
Smartlook.init(projectId, { region: 'eu' })

const ButtonWrapper = styled(FormSubmit)`
  position: absolute;
  bottom: 32px;
  width: calc(100% - 40px);
`

const BookWithReferral = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'bookWithReferral',
  })
  const { trackFormError, trackFormInteraction } = useTrackFormProgress(
    XpertFormStep.ReferralCodeTrue,
  )

  const [params] = useSearchParams()
  const { dateOfBirth, setPatientData, setReferralCode, zdCode } =
    useAppointmentStore((state) => ({
      dateOfBirth: state.patientData?.dateOfBirth || params.get('DOB') || '',
      setPatientData: state.setPatientData,
      setReferralCode: state.setReferralCode,
      zdCode: state.zdCode || params.get('ZD') || '',
    }))

  const actionData = useActionData() as ActionData | undefined

  useEffect(() => {
    const error = actionData?.errors
      ? actionData.errors.map((e) => e.message).join(',')
      : actionData?.error
    if (error) {
      trackFormError({
        error,
      })
    }
  }, [actionData?.errors, actionData?.error])

  return (
    <ScreenWrapper>
      <FormStyled method="post" align="center">
        <Header>{t('header')}</Header>
        <FormElement>
          <Input
            label={t('referralCode.label')}
            type="text"
            name="referralCode"
            value={zdCode}
            placeholder={t('referralCode.placeholder')}
            onBlur={(e) => {
              const value = e.target.value
              if (value && value.match(/^\d*$/)) {
                setReferralCode(`ZD${value}`)
              }
            }}
            onFocus={() => {
              trackFormInteraction({
                action: 'click',
                formFieldName: 'referralCode',
              })
            }}
            onChange={(e) => setReferralCode(e.target.value)}
            errorMessage={
              actionData?.errors?.find((e) => e.path[0] === 'referralCode')
                ? t('errorMessage.referralCode')
                : undefined
            }
          />
        </FormElement>

        <FormElement>
          <Datepicker
            name="dateOfBirth"
            label={t('dateOfBirth.label')}
            placeholder={t('dateOfBirth.placeholder')}
            defaultValue={dateOfBirth}
            onChange={(value) => {
              setPatientData({ dateOfBirth: value })
              if (value) {
                trackFormInteraction({
                  action: 'click',
                  formFieldName: 'dateOfBirth',
                })
              }
            }}
            errorMessage={
              actionData?.errors?.find((e) => e.path[0] === 'dateOfBirth')
                ? t('errorMessage.dateOfBirth')
                : undefined
            }
          />
        </FormElement>
        <InputCaption>{t('caption')}</InputCaption>
        {actionData?.error && (
          <NotificationError label={t(`errorMessage.${actionData.error}`)} />
        )}

        <ButtonWrapper>
          <StyledActionButton
            variant="Primary"
            type="submit"
            disabled={!zdCode || !dateOfBirth}
            onClick={() => {
              trackFormInteraction({
                action: 'next',
                formFieldName: 'submit',
              })
            }}
          >
            {t('actions.checkReferralCode')}
          </StyledActionButton>
        </ButtonWrapper>
      </FormStyled>
    </ScreenWrapper>
  )
}

export { BookWithReferral }
